import type React from 'react';
import { useLayoutEffect, useState } from 'react';

import { useAppliedTheme } from '@xing-com/hub';
import { Headline, Meta } from '@xing-com/typography';
import { BREAKPOINTS, useMediaListener } from '@xing-com/util';

import type {
  featureProps,
  featuresTableProps,
  generateTipsAndTricksActionUrlTrackingParamsType,
  generateUrlTrackingParamsType,
  onTipsAndTricksButtonClickType,
  onTipsAndTricksButtonCloseClickType,
  onTipsAndTricksVideoTrackingType,
  setOpenExtendersType,
} from '../../types';
import getMatchedMedia from '../../utils/get-matched-media';
import getTextWidth from '../../utils/get-text-width';
import { TipsAndTricks } from '../tips-and-tricks';

import { Comparison } from './comparison';
import { FeaturesTableActions } from './features-table-actions';
import { FeaturesTableHeaders } from './features-table-headers';
import { Styled } from './styles';

type TextSize = 'bold 16px XING Sans XDL' | 'bold 14px XING Sans XDL';

const getTexSizeWithMatchedMedia = (matchedMedia: string): TextSize => {
  switch (matchedMedia) {
    case 'nav':
    case 'desktopwide':
    case 'desktop':
    case 'tabletwide':
    case 'tablet':
      return 'bold 16px XING Sans XDL';
    case 'handheld':
    case 'mobile':
    default:
      return 'bold 14px XING Sans XDL';
  }
};

const renderFeaturesTableHeaders = (
  featuresTableHeaders: Array<string> | React.ReactNode[],
  featuresTableHeadersWidth: Array<string>
): React.JSX.Element => (
  <Styled.GroupTableHeader
    style={{
      gridTemplateColumns: `1fr ${featuresTableHeadersWidth.join(' ')}`,
    }}
  >
    <FeaturesTableHeaders data={featuresTableHeaders} />
  </Styled.GroupTableHeader>
);

const renderTabGroupItems = (
  feature: featureProps,
  featuresTableHeadersWidth: Array<string>
): React.JSX.Element => (
  <Styled.Feature
    style={{
      gridTemplateColumns: `1fr ${featuresTableHeadersWidth.join(' ')}`,
    }}
    key={feature.text}
  >
    <Styled.FeatureText size="small" sizeConfined="medium">
      {feature.specialText && (
        <Styled.FeatureSpecialText data-testid="feature-specialtext">
          {feature.specialText}:{' '}
        </Styled.FeatureSpecialText>
      )}
      {feature.text}
    </Styled.FeatureText>
    {feature.values &&
      feature.values.map((value, index) => (
        <Styled.FeatureValue
          size="small"
          sizeConfined="medium"
          noMargin
          key={`${value}-${index}`}
        >
          <Comparison value={value} />
        </Styled.FeatureValue>
      ))}
  </Styled.Feature>
);

const calculateFeaturesTableHeadersWidth = (
  featuresTableHeaders: Array<string> | React.ReactNode[],
  matchedMedia: string
): Array<string> =>
  featuresTableHeaders.map((tableHeader) => {
    const tableHeaderValue =
      typeof tableHeader === 'string'
        ? tableHeader
        : tableHeader?.valueOf().toString();
    return (
      Math.ceil(
        getTextWidth(
          tableHeaderValue ?? '',
          getTexSizeWithMatchedMedia(matchedMedia)
        )
      ) + 'px'
    );
  });

export type FeaturesTableProps = {
  data: featuresTableProps;
  activeTab: number;
  featuresTableHeaders: Array<string> | React.ReactNode[];
  openExtenders: Record<string, unknown>;
  setOpenExtenders: setOpenExtendersType;
  hideActions?: boolean;
  hideTipsAndTricks?: boolean;
  onTipsAndTricksButtonClick?: onTipsAndTricksButtonClickType;
  onTipsAndTricksButtonCloseClick?: onTipsAndTricksButtonCloseClickType;
  onTipsAndTricksVideoTracking?: onTipsAndTricksVideoTrackingType;
  generateTipsAndTricksActionUrlTrackingParams?: generateTipsAndTricksActionUrlTrackingParamsType;
  generateUrlTrackingParams?: generateUrlTrackingParamsType;
};

export const FeaturesTable: React.FC<FeaturesTableProps> = ({
  data,
  activeTab,
  featuresTableHeaders,
  openExtenders,
  setOpenExtenders,
  hideActions,
  hideTipsAndTricks,
  onTipsAndTricksButtonClick = () => ({}),
  onTipsAndTricksButtonCloseClick = () => ({}),
  generateTipsAndTricksActionUrlTrackingParams,
  onTipsAndTricksVideoTracking,
  generateUrlTrackingParams,
}) => {
  const breakpoint = useMediaListener();
  const isHandheld = breakpoint === BREAKPOINTS.handheld;
  const isDesktop = breakpoint !== BREAKPOINTS.handheld;

  const matchedMedia = getMatchedMedia(breakpoint);

  const isDarkTheme = useAppliedTheme() === 'dark';

  const [featuresTableHeadersWidth, setFeaturesTableHeadersWidth] = useState<
    Array<string>
  >(['']);

  useLayoutEffect(() => {
    const featuresTableHeadersWidth = calculateFeaturesTableHeadersWidth(
      featuresTableHeaders,
      getMatchedMedia(breakpoint)
    );

    setFeaturesTableHeadersWidth(featuresTableHeadersWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchedMedia]);

  return (
    <>
      {isDesktop &&
        renderFeaturesTableHeaders(
          featuresTableHeaders,
          featuresTableHeadersWidth
        )}
      <Styled.FeatureList>
        {data.map(
          (content, tabIndex) =>
            tabIndex === activeTab &&
            content?.groups?.map((group, groupIndex) => {
              const tabTrackingId = content.trackingId;
              const groupTrackingId = group?.trackingId;

              return (
                <Styled.FeatureListElement key={'container-' + group?.header}>
                  {isHandheld && (
                    <>
                      <div key={group?.header}>
                        <Styled.GroupHeader>
                          <Styled.GroupImage>
                            <img
                              src={
                                !isDarkTheme
                                  ? group?.imageUrl || ''
                                  : group?.darkImageUrl || group?.imageUrl || ''
                              }
                              alt={''}
                              aria-hidden="true"
                            />
                          </Styled.GroupImage>
                          <div>
                            <Headline
                              size="xlarge"
                              noMargin
                              sizeConfined="xxlarge"
                              data-testid={`header-tab-${tabIndex}-group-${groupIndex}`}
                            >
                              {group?.header}
                            </Headline>
                            {group?.subheader && <Meta>{group.subheader}</Meta>}
                          </div>
                        </Styled.GroupHeader>
                      </div>
                      {renderFeaturesTableHeaders(
                        featuresTableHeaders,
                        featuresTableHeadersWidth
                      )}
                    </>
                  )}

                  <Styled.GroupContainer
                    data-testid={`tab-${tabIndex}-group-container-${groupIndex}`}
                  >
                    <Styled.GroupContentContainer>
                      {isDesktop && (
                        <Styled.GroupImage>
                          <img
                            src={
                              !isDarkTheme
                                ? group?.imageUrl || ''
                                : group?.darkImageUrl || group?.imageUrl || ''
                            }
                            alt={''}
                            aria-hidden="true"
                          />
                        </Styled.GroupImage>
                      )}
                      <Styled.GroupContent data-testid={`group-${groupIndex}`}>
                        {isDesktop && (
                          <Styled.GroupHeaderContainer>
                            <Styled.GroupHeaderHeadline
                              size="xlarge"
                              noMargin
                              style={{
                                gridTemplateColumns: `1fr ${featuresTableHeadersWidth?.join(
                                  ' '
                                )}`,
                              }}
                            >
                              {group?.header}
                            </Styled.GroupHeaderHeadline>
                            {group?.subheader && (
                              <Meta
                                noMargin
                                style={{
                                  gridTemplateColumns: `1fr ${featuresTableHeadersWidth?.join(
                                    ' '
                                  )}`,
                                }}
                              >
                                {group.subheader}
                              </Meta>
                            )}
                          </Styled.GroupHeaderContainer>
                        )}
                        {group?.items?.map((feature) =>
                          renderTabGroupItems(
                            feature,
                            featuresTableHeadersWidth
                          )
                        )}
                        {!hideActions && (
                          <FeaturesTableActions
                            hideTipsAndTricks={hideTipsAndTricks}
                            group={group}
                            groupIndex={groupIndex}
                            tabIndex={tabIndex}
                            generateUrlTrackingParams={
                              generateUrlTrackingParams
                            }
                            openExtenders={openExtenders}
                            setOpenExtenders={setOpenExtenders}
                            onTipsAndTricksButtonClick={
                              onTipsAndTricksButtonClick
                            }
                          />
                        )}
                      </Styled.GroupContent>
                    </Styled.GroupContentContainer>
                    {!hideActions &&
                      !hideTipsAndTricks &&
                      group?.tipsAndTricks &&
                      group.tipsAndTricks[0] && (
                        <Styled.TipsAndTricksCollapseContainer
                          isOpened={Boolean(openExtenders[group.header || ''])}
                          key={group.header}
                        >
                          <>
                            <TipsAndTricks
                              {...group.tipsAndTricks[0]}
                              tabTrackingId={tabTrackingId || ''}
                              groupTrackingId={groupTrackingId || ''}
                              hideButtonsFromFocus={Boolean(
                                openExtenders[group.header || '']
                              )}
                              onButtonCloseClick={() => {
                                if (!onTipsAndTricksButtonCloseClick) {
                                  return;
                                }
                                onTipsAndTricksButtonCloseClick(
                                  tabTrackingId || '',
                                  groupTrackingId || ''
                                );
                                setOpenExtenders({
                                  ...openExtenders,
                                  [group.header || '']: false,
                                });
                              }}
                              onVideoTracking={onTipsAndTricksVideoTracking}
                              generateActionUrlTrackingParams={
                                generateTipsAndTricksActionUrlTrackingParams
                              }
                              videoId={group.tipsAndTricks[0].videoId || 0}
                            />
                          </>
                        </Styled.TipsAndTricksCollapseContainer>
                      )}
                  </Styled.GroupContainer>
                </Styled.FeatureListElement>
              );
            })
        )}
      </Styled.FeatureList>
    </>
  );
};
